import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { Close, InfoOutlined, Message, Phone, Send } from '@mui/icons-material';
import { Box, Divider, Drawer, IconButton, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AppButton } from '../../../shared/components/buttons/AppButton';
import offerDetailService from '../services/offerDetailService';
import { useSnackbar } from '../../../shared/hooks/useSnackbar';
import { BACKDOOR_TOKEN_KEY } from '../../../shared/constants/auth';
import { useParentApp } from '../../../shared/hooks/useParentApp';

interface ContactSellerModalProps {
  open: boolean;
  onClose: () => void;
  offerId?: string;
  sellerName?: string;
  phoneNumber?: string | null;
}

export default function ContactSellerModal({
  open,
  onClose,
  offerId,
  sellerName,
  phoneNumber,
}: Readonly<ContactSellerModalProps>) {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const { isInsideIframe, requestPhoneCall } = useParentApp();
  const [hasBackdoorToken, setHasBackdoorToken] = useState(false);
  const [activeTab, setActiveTab] = useState<'message' | 'phone'>('message');
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const displayName = sellerName || t('offerDetail.contactSellerFallback');
  const phoneHref = phoneNumber ? `tel:${phoneNumber.split(' ').join('')}` : undefined;
  const maxLength = 500;
  const isAuthorized = hasBackdoorToken;
  const trimmedMessage = message.trim();
  const canSend = isAuthorized && trimmedMessage.length > 0 && !isSending;
  const counterLabel = useMemo(() => `${message.length}/${maxLength}`, [message.length]);

  useEffect(() => {
    if (globalThis.window === undefined) {
      return;
    }

    const token =
      sessionStorage.getItem(BACKDOOR_TOKEN_KEY) || localStorage.getItem(BACKDOOR_TOKEN_KEY);
    setHasBackdoorToken(Boolean(token));
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    setActiveTab('message');
    setMessage(t('offerDetail.messagePrefill'));
    setErrorText(null);
  }, [open, t]);

  const handleMessageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value.length > maxLength) {
      setMessage(value.slice(0, maxLength));
      return;
    }
    setMessage(value);
    if (errorText) {
      setErrorText(null);
    }
  };

  const handleSendMessage = async () => {
    if (!offerId) {
      const message = t('offerDetail.messageMissingOffer');
      setErrorText(message);
      showSnackbar(message, { variant: 'error' });
      return;
    }

    if (!isAuthorized) {
      const message = t('offerDetail.messageAuthRequired');
      setErrorText(message);
      showSnackbar(message, { variant: 'warning' });
      return;
    }

    try {
      setIsSending(true);
      await offerDetailService.sendMessage(offerId, trimmedMessage);
      showSnackbar(t('offerDetail.messageSendSuccess'), { variant: 'success' });
      onClose();
    } catch (error: unknown) {
      const apiError = error as { response?: { status?: number } };
      if (apiError.response?.status === 401) {
        const message = t('offerDetail.messageAuthRequired');
        setErrorText(message);
        showSnackbar(message, { variant: 'warning' });
      } else if (apiError.response?.status === 400) {
        const message = t('offerDetail.messageSelfError');
        setErrorText(message);
        // Don't show snackbar - error is already displayed in bottomsheet
      } else {
        const message = t('offerDetail.messageSendError');
        setErrorText(message);
        showSnackbar(message, { variant: 'error' });
      }
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            px: 2.5,
            pt: 2,
            pb: 3,
          },
        },
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1" fontWeight={600}>
            {t('offerDetail.contactSellerTitle', { name: displayName })}
          </Typography>
          <IconButton aria-label={t('offerDetail.closeModal')} onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
        <Divider />
        <Box sx={{ bgcolor: 'grey.100', borderRadius: 2, p: 0.5 }}>
          <Stack direction="row" spacing={1}>
            <AppButton
              variant={activeTab === 'message' ? 'primary' : 'outlined'}
              fullWidth
              startIcon={<Message />}
              sx={{ flex: 1, py: 1 }}
              onClick={() => setActiveTab('message')}
            >
              {t('offerDetail.contactTabs.message')}
            </AppButton>
            <AppButton
              variant={activeTab === 'phone' ? 'primary' : 'outlined'}
              fullWidth
              startIcon={<Phone />}
              sx={{ flex: 1, py: 1 }}
              onClick={() => setActiveTab('phone')}
            >
              {t('offerDetail.contactTabs.phone')}
            </AppButton>
          </Stack>
        </Box>
        {activeTab === 'message' ? (
          <Stack spacing={1.5} alignItems="stretch">
            <Typography variant="body2" color="text.secondary">
              {t('offerDetail.messageFieldLabel')}
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              value={message}
              onChange={handleMessageChange}
              disabled={!isAuthorized || isSending}
            />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" color="text.secondary">
                {counterLabel}
              </Typography>
              {!isAuthorized && (
                <Typography variant="caption" color="warning.main">
                  {t('offerDetail.messageAuthRequired')}
                </Typography>
              )}
            </Stack>
            {errorText && (
              <Typography variant="caption" color="error.main">
                {errorText}
              </Typography>
            )}
            <AppButton
              variant="primary"
              fullWidth
              startIcon={<Send />}
              onClick={handleSendMessage}
              disabled={!canSend}
              sx={{ py: 1.25 }}
            >
              {t('offerDetail.messageSendButton')}
            </AppButton>
          </Stack>
        ) : (
          <Stack spacing={1.5} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {t('offerDetail.sellerPhoneLabel')}
            </Typography>
            {phoneNumber ? (
              <>
                <AppButton
                  variant="primary"
                  fullWidth
                  startIcon={<Phone />}
                  onClick={() => {
                    if (phoneHref) {
                      if (isInsideIframe) {
                        requestPhoneCall(phoneNumber.split(' ').join(''));
                      } else {
                        window.location.href = phoneHref;
                      }
                    }
                  }}
                  sx={{ py: 1.25 }}
                >
                  {phoneNumber}
                </AppButton>
                <Typography variant="caption" color="text.secondary">
                  {t('offerDetail.callHint')}
                </Typography>
              </>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  borderRadius: 2,
                  bgcolor: 'grey.100',
                  px: 2,
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <InfoOutlined color="action" fontSize="small" />
                <Typography variant="body2" color="text.secondary">
                  {t('offerDetail.phoneUnavailable')}
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </Stack>
    </Drawer>
  );
}
